// File: src/components/SiteFooter.jsx
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Smartphone, X } from "lucide-react";
import { useInstallPrompt, isIOSDevice, isAppInstalled } from "../pwa";
import NewsletterFooterSignup from "./NewsletterFooterSignup";

export default function SiteFooter() {
  const currentYear = new Date().getFullYear();
  const { canInstall, installApp, isInstalled } = useInstallPrompt();
  const [showIOSModal, setShowIOSModal] = useState(false);

  const isIOS = isIOSDevice();
  const alreadyInstalled = isInstalled || isAppInstalled();

  // Handle install click
  const handleInstallClick = async () => {
    if (isIOS) {
      setShowIOSModal(true);
    } else if (canInstall) {
      await installApp();
    } else {
      // Fallback: show instructions
      setShowIOSModal(true);
    }
  };

  return (
    <>
      <footer className="w-full py-8 px-6 border-t border-slate-800/50 bg-transparent mt-auto">
        <div className="max-w-7xl mx-auto flex flex-col items-center gap-4 text-center">
          <NewsletterFooterSignup />
          <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6">
            <Link to="/terms" className="text-[10px] uppercase tracking-widest text-slate-400 hover:text-yellow-500 transition-colors font-black">
              Terms & Conditions
            </Link>
            <Link to="/privacy" className="text-[10px] uppercase tracking-widest text-slate-400 hover:text-yellow-500 transition-colors font-black">
              Privacy Policy
            </Link>
            <Link to="/refund-policy" className="text-[10px] uppercase tracking-widest text-slate-400 hover:text-yellow-500 transition-colors font-black">
              Refund Policy
            </Link>
            <Link to="/contact" className="text-[10px] uppercase tracking-widest text-slate-400 hover:text-yellow-500 transition-colors font-black">
              Contact
            </Link>
            <Link to="/careers" className="text-[10px] uppercase tracking-widest text-slate-400 hover:text-yellow-500 transition-colors font-black">
              Careers
            </Link>
            {/* Install App Link - only show if not already installed */}
            {!alreadyInstalled && (
              <button
                onClick={handleInstallClick}
                className="text-[10px] uppercase tracking-widest text-yellow-500 hover:text-yellow-400 transition-colors font-black flex items-center gap-1"
              >
                <Smartphone size={12} />
                Install App
              </button>
            )}
          </div>

          {/* Social Icons */}
          <div className="flex items-center gap-4">
            {[
              { label: "Facebook", href: "https://www.facebook.com/thescope180", svg: "M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z" },
              { label: "Instagram", href: "https://www.instagram.com/thescope180", svg: "M16 4H8a4 4 0 00-4 4v8a4 4 0 004 4h8a4 4 0 004-4V8a4 4 0 00-4-4zm-4 11a3 3 0 110-6 3 3 0 010 6zm4.5-7.5a1 1 0 110-2 1 1 0 010 2z" },
              { label: "LinkedIn", href: "https://www.linkedin.com/company/thescope180", svg: "M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-4 0v7h-4v-7a6 6 0 016-6zM2 9h4v12H2zM4 6a2 2 0 110-4 2 2 0 010 4z" },
              { label: "X", href: "https://x.com/thescope180", svg: "M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" },
              { label: "YouTube", href: "https://www.youtube.com/@thescope180", svg: "M22.54 6.42a2.78 2.78 0 00-1.96-1.96C18.88 4 12 4 12 4s-6.88 0-8.58.46a2.78 2.78 0 00-1.96 1.96A29.94 29.94 0 001 12a29.94 29.94 0 00.46 5.58 2.78 2.78 0 001.96 1.96C5.12 20 12 20 12 20s6.88 0 8.58-.46a2.78 2.78 0 001.96-1.96A29.94 29.94 0 0023 12a29.94 29.94 0 00-.46-5.58zM9.75 15.02V8.98L15.5 12l-5.75 3.02z" },
              { label: "TikTok", href: "https://www.tiktok.com/@thescope180", svg: "M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 00-.79-.05A6.34 6.34 0 003.15 15a6.34 6.34 0 0010.86-4.43V7.13a8.16 8.16 0 005.58 2.18V5.86a4.83 4.83 0 01-3.77-.86z" /* intentionally not closing here */ },
              { label: "Google", href: "https://www.google.com/search?q=TheScope180", svg: "M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1zM12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23zM5.84 14.09A6.61 6.61 0 015.5 12c0-.73.13-1.43.34-2.09V7.07H2.18A11 11 0 001 12c0 1.77.43 3.45 1.18 4.93l3.66-2.84zM12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" },
            ].map(({ label, href, svg }) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-slate-500 hover:text-yellow-500 transition-colors"
                title={label}
              >
                <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                  <path d={svg} />
                </svg>
              </a>
            ))}
            <Link
              to="/articles"
              className="text-slate-500 hover:text-yellow-500 transition-colors font-black text-sm"
              title="Articles"
            >
              A
            </Link>
            <Link
              to="/socials"
              className="text-[10px] uppercase tracking-widest text-slate-400 hover:text-yellow-500 transition-colors font-black ml-1"
            >
              Follow Us
            </Link>
          </div>
          <p className="text-[9px] text-slate-500 max-w-xl">
            Some courses use AI-generated avatars and AI voice narration in presentations and self-paced content.
            Live instructor-led courses are excluded. See our{" "}
            <Link to="/terms" className="text-slate-400 underline hover:text-yellow-500">Terms</Link> for details.
          </p>
          <p className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">
            © {currentYear} TheScope180, LLC. TheScope180™ is a trademark of TheScope180, LLC.
          </p>
        </div>
      </footer>

      {/* iOS Install Instructions Modal */}
      {showIOSModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 max-w-sm w-full shadow-2xl relative">
            <button
              onClick={() => setShowIOSModal(false)}
              className="absolute top-4 right-4 p-1 rounded-full hover:bg-gray-100 dark:hover:bg-slate-800"
            >
              <X size={20} className="text-gray-500" />
            </button>

            <div className="text-center mb-4">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center mx-auto mb-3">
                <Smartphone size={28} className="text-white" />
              </div>
              <h3 className="text-lg font-black text-gray-900 dark:text-white">
                Install TheScope180
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Add to your home screen for the best experience
              </p>
            </div>

            {isIOS ? (
              <div className="space-y-3 text-sm text-gray-600 dark:text-gray-300">
                <div className="flex items-start gap-3 p-3 rounded-lg bg-gray-50 dark:bg-slate-800">
                  <span className="font-bold text-yellow-500">1.</span>
                  <span>Tap the <strong>Share</strong> button (box with arrow) at the bottom of Safari</span>
                </div>
                <div className="flex items-start gap-3 p-3 rounded-lg bg-gray-50 dark:bg-slate-800">
                  <span className="font-bold text-yellow-500">2.</span>
                  <span>Scroll down and tap <strong>"Add to Home Screen"</strong></span>
                </div>
                <div className="flex items-start gap-3 p-3 rounded-lg bg-gray-50 dark:bg-slate-800">
                  <span className="font-bold text-yellow-500">3.</span>
                  <span>Tap <strong>"Add"</strong> in the top right corner</span>
                </div>
              </div>
            ) : (
              <div className="space-y-3 text-sm text-gray-600 dark:text-gray-300">
                <div className="flex items-start gap-3 p-3 rounded-lg bg-gray-50 dark:bg-slate-800">
                  <span className="font-bold text-yellow-500">1.</span>
                  <span>Tap the <strong>menu icon</strong> (three dots) in your browser</span>
                </div>
                <div className="flex items-start gap-3 p-3 rounded-lg bg-gray-50 dark:bg-slate-800">
                  <span className="font-bold text-yellow-500">2.</span>
                  <span>Tap <strong>"Install app"</strong> or <strong>"Add to Home Screen"</strong></span>
                </div>
                <div className="flex items-start gap-3 p-3 rounded-lg bg-gray-50 dark:bg-slate-800">
                  <span className="font-bold text-yellow-500">3.</span>
                  <span>Confirm by tapping <strong>"Install"</strong></span>
                </div>
              </div>
            )}

            <button
              onClick={() => setShowIOSModal(false)}
              className="w-full mt-4 py-3 rounded-xl bg-yellow-500 hover:bg-yellow-400 text-black font-bold transition-colors"
            >
              Got it!
            </button>
          </div>
        </div>
      )}
    </>
  );
}